/**
 * Picking rows out of a review list, and saying which ones failed.
 *
 * Written identically twice before this existed — once in SnapshotReviewModal
 * and once in CarrierPaymentsReviewModal — and a third copy is how two screens
 * that should behave the same stop doing so. The one difference between the two
 * copies was itself the bug: one marked a failed row in place with its reason,
 * the other reported a bare count in a toast and left the person to guess which.
 *
 * A row is selectable only while it is PENDING: anything already applied or
 * dismissed is history, and offering a tick against it would promise an action
 * that the server will refuse.
 */
export default {
  data() {
    return {
      selected: [],
      /** rowId -> why applying it did not work, shown on the row itself. */
      failures: {},
    };
  },
  computed: {
    /** Every loaded row a person is still allowed to decide about. */
    selectable() {
      return (this.rows || [])
        .filter((r) => r.reviewStatus === 'PENDING')
        .map((r) => r.id);
    },
    /**
     * How many rows "all" actually means.
     *
     * Defaults to what is loaded, which is right for a list that loads in one
     * go. A paged list must override it with the size of the whole group, or
     * select-all ticks the first page while the label beside it counts the rest
     * — which is exactly what it did.
     */
    selectionTotal() {
      return this.selectable.length;
    },
    allSelected() {
      return this.selectionTotal > 0 && this.selected.length >= this.selectionTotal;
    },
    someSelected() {
      return this.selected.length > 0 && !this.allSelected;
    },
  },
  methods: {
    toggle(id) {
      this.selected = this.selected.includes(id)
        ? this.selected.filter((x) => x !== id)
        : this.selected.concat(id);
    },
    /** Overridden where "all" is more than what is on screen. */
    selectAll() {
      this.selected = this.selectable.slice();
      return Promise.resolve(this.selected);
    },
    toggleAll() {
      if (this.allSelected) { this.clearSelection(); return Promise.resolve([]); }
      return this.selectAll();
    },
    clearSelection() {
      this.selected = [];
    },
    /**
     * Keep the rows that failed on screen with their reasons, and forget the
     * rest. A failure stays PENDING server-side, so it is still the person's to
     * deal with and must still be findable.
     *
     * @param results [{ id, status, message }] from a resolve call
     * @returns how many actually went through
     */
    recordFailures(results) {
      const failed = {};
      (results || [])
        .filter((r) => r.status === 'FAILED')
        .forEach((r) => {
          failed[r.id] = r.message || this.$t('adminTitles.someError');
        });
      this.failures = failed;
      return (results || []).length - Object.keys(failed).length;
    },
  },
};
